"use client";

import { useLingui } from "@lingui/react/macro";
import { LicensePlate } from "@offstreetparking/design-system/dataDisplay/LicensePlate";
import { Alert } from "@offstreetparking/design-system/feedback";
import { BaseRadio, Button } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { useState } from "react";
import { offstreetClient } from "~/services/offstreetApi";
import { OverlayShell } from "./OverlayShell";

export const SelectDefaultVehiclePrompt = () => {
	const { t } = useLingui();
	const utils = offstreetClient.useUtils();
	const [selectedVehicleId, setSelectedVehicleId] = useState<string>();

	const {
		data,
		error: vehiclesError,
		refetch,
	} = offstreetClient.parkers.getParkerVehicles.useQuery(undefined, {
		refetchOnWindowFocus: false,
	});
	const vehicles = data?.items ?? [];
	const selectedVehicle = vehicles.find((vehicle) => String(vehicle.id) === selectedVehicleId) ?? vehicles[0];

	const {
		mutate: saveDefaultVehicle,
		isPending: isSaving,
		error: saveError,
	} = offstreetClient.parkers.updateParkerVehicle.useMutation({
		onSuccess: () => utils.parkers.invalidate(),
	});

	if (vehiclesError) {
		return (
			<OverlayShell label={t`Choose your default vehicle`}>
				<Alert
					type="error"
					title={t`Something went wrong. Please try again.`}
					message={vehiclesError.message}
				/>
				<Button className="w-full" intent="primary" size="lg" onClick={() => void refetch()}>
					{t`Retry`}
				</Button>
			</OverlayShell>
		);
	}

	if (!selectedVehicle || vehicles.length < 2) {
		return null;
	}

	return (
		<OverlayShell label={t`Choose your default vehicle`}>
			<Heading As="h1" kind="h1" className="font-bold text-3xl leading-tight sm:text-4xl">
				{t`Choose your default vehicle`}
			</Heading>
			<Text intent="muted">
				{t`Your default vehicle is selected automatically the next time you register for parking. You can change it anytime.`}
			</Text>
			{saveError && (
				<Alert type="error" title={t`Something went wrong. Please try again.`} message={saveError.message} />
			)}
			<BaseRadio
				name="default-vehicle"
				aria-label={t`Default vehicle`}
				value={String(selectedVehicle.id)}
				onChange={setSelectedVehicleId}
				size="lg"
				className="w-full space-y-4 text-left"
				itemClassName="items-stretch gap-0 rounded-lg border border-neutral bg-white has-data-checked:border-primary has-data-checked:bg-primary-faded"
				radioClassName="cursor-pointer p-4"
				options={vehicles.map((vehicle) => ({
					value: String(vehicle.id),
					adjacent: null,
					content: (
						<>
							<LicensePlate plate={vehicle.plate} state={vehicle.state} className="shrink-0" />
							<Text As="p" weight="medium" className="min-w-0 flex-1 truncate">
								{vehicle.nickname || t`Untitled Vehicle`}
							</Text>
						</>
					),
				}))}
			/>
			<Button
				className="w-full sm:w-auto sm:min-w-40"
				intent="primary"
				size="lg"
				isLoading={isSaving}
				onClick={() => {
					saveDefaultVehicle({
						vehicleId: selectedVehicle.id,
						plate: selectedVehicle.plate,
						state: selectedVehicle.state,
						nickname: selectedVehicle.nickname,
						isDefault: true,
					});
				}}
			>
				{t`Save default vehicle`}
			</Button>
		</OverlayShell>
	);
};
